import React, { useEffect, useState } from 'react';
import styles from './index.module.scss';
import ActionButton from 'components/ActionButton';
import _ from 'lodash';
import { updateSalesChannelMappingDetails } from 'services/api/salesChannel';
import { getImageUrlFromName, showError, showSuccess, stringifyAndParseObj } from 'utils';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers';
import { useForm } from 'react-hook-form';
import ImageUploadComponent from 'components/ImageUploadComponent';
import RenderInfo from 'components/RenderInfo';

const validationSchema = yup.object().shape({
  inventoryItemName: yup.string(),
  inventoryItemCategoryName: yup.string(),
  inventoryItemSubCategoryName: yup.string(),
  inventoryItemDescription: yup.string(),
  inventoryItemImage: yup.object(),
});

const InventoryItem = (props) => {
  const { itemDetails, setItemDetails } = props;
  const { register, handleSubmit, setValue } = useForm<FormData>({
    resolver: yupResolver(validationSchema),
  });

  const [loading, setLoading] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState(false);
  const [imageForPreview, setImageForPreview] = useState<string>('');

  const inventoryItemName = _.get(
    itemDetails,
    'inventoryItemName',
    _.get(itemDetails, 'inventoryItem.name', '-'),
  );
  const inventoryItemDescription = _.get(
    itemDetails,
    'inventoryItemDescription',
    _.get(itemDetails, 'inventoryItem.description', '-'),
  );
  const inventoryItemCategoryName = _.get(
    itemDetails,
    'inventoryItemCategoryName',
    _.get(itemDetails, 'inventoryItem.category.group.name', '-'),
  );
  const inventoryItemSubCategoryName = _.get(
    itemDetails,
    'inventoryItemSubCategoryName',
    _.get(itemDetails, 'inventoryItem.category.description', '-'),
  );
  const inventoryItemImageName = _.get(
    itemDetails,
    'inventoryItemImage.name',
    _.get(itemDetails, 'inventoryItem.image.name', '-'),
  );

  useEffect(() => {
    register('inventoryItemImage');
    setValue('inventoryItemName', inventoryItemName);
    setValue('inventoryItemDescription', inventoryItemDescription);
    setValue('inventoryItemCategoryName', inventoryItemCategoryName);
    setValue('inventoryItemSubCategoryName', inventoryItemSubCategoryName);
    if (inventoryItemImageName) {
      setImageForPreview(getImageUrlFromName(inventoryItemImageName));
    }
  }, [itemDetails]);

  const updateSalesChannelMappingDetailsFn = handleSubmit(
    async (values: any) => {
      setLoading(true);
      try {
        let response = await updateSalesChannelMappingDetails({
          id: itemDetails?.objectId,
          params: values,
        });
        response = stringifyAndParseObj(response);
        setItemDetails(response);
        setIsEdit(false);
        showSuccess('Details saved successfully');
      } catch (e) {
        showError(e);
      } finally {
        setLoading(false);
      }
    },
  );

  const onClickCancel = () => {
    setIsEdit(false);
  };

  const getInventoryItemEditButtons = () => {
    if (isEdit) {
      return (
        <div className="d-flex">
          <ActionButton
            text={'Cancel'}
            active={true}
            isLoading={false}
            onClick={onClickCancel}
            customClass={''}
          />
          <ActionButton
            text={'Save'}
            active={!loading}
            isLoading={loading}
            onClick={updateSalesChannelMappingDetailsFn}
            customClass={'ml-2'}
          />
        </div>
      );
    }

    return (
      <ActionButton
        text={'Edit'}
        active={true}
        isLoading={false}
        onClick={() => {
          setIsEdit(true);
        }}
        customClass={''}
      />
    );
  };

  const getName = () => {
    return (
      <div className={styles['input-group']}>
        <div className={styles['label']}>Name</div>
        <input
          name={'inventoryItemName'}
          className={'form-control'}
          ref={register}
          disabled={loading}
          style={{ display: isEdit ? 'block' : 'none' }}
        />
        <div style={{ display: isEdit ? 'none' : 'block' }}>
          {inventoryItemName}
        </div>
      </div>
    );
  };

  const getDescription = () => {
    return (
      <div className={styles['input-group']}>
        <div className={styles['label']}>Description</div>
        <textarea
          name={'inventoryItemDescription'}
          className={'form-control'}
          ref={register}
          disabled={loading}
          style={{ display: isEdit ? 'block' : 'none' }}
        />
        <div style={{ display: isEdit ? 'none' : 'block' }}>
          {inventoryItemDescription}
        </div>
      </div>
    );
  };

  const getSubCategory = () => {
    return (
      <div className={styles['input-group']}>
        <div className={styles['label']}>Sub Category</div>
        <input
          name={'inventoryItemSubCategoryName'}
          className={'form-control'}
          ref={register}
          disabled={loading}
          style={{ display: isEdit ? 'block' : 'none' }}
        />
        <div style={{ display: isEdit ? 'none' : 'block' }}>
          {inventoryItemSubCategoryName}
        </div>
      </div>
    );
  };

  const getCategory = () => {
    return (
      <div className={styles['input-group']}>
        <div className={styles['label']}>Category</div>
        <input
          name={'inventoryItemCategoryName'}
          className={'form-control'}
          ref={register}
          disabled={loading}
          style={{ display: isEdit ? 'block' : 'none' }}
        />
        <div style={{ display: isEdit ? 'none' : 'block' }}>
          {inventoryItemCategoryName}
        </div>
      </div>
    );
  };

  const onImageUpload = (id, image) => {
    setValue('inventoryItemImage', image);
    setImageForPreview(image?._url);
    setIsEdit(true);
  };

  const getImageInput = () => {
    return(
      <div className={styles['image-container']}>
        <ImageUploadComponent
          src={imageForPreview.toString()}
          uploadAPI={onImageUpload}
          onFileSelect={setImageForPreview}
          forceShowUploadButton
        />
      </div>
    );
  };

  return (
    <div className={styles['section-container']}>
      <div className="d-flex justify-content-between">
        <div className={styles['heading']}>Inventory Item</div>
        {getInventoryItemEditButtons()}
      </div>
      <div className='d-flex'>
        {getImageInput()}
        <div className='d-flex flex-row justify-content-between w-100 ml-5'>
          <div className='w-100 mr-5'>
            {getName()}
            {getDescription()}
            {getCategory()}
            {getSubCategory()}
          </div>
          <div>
            <RenderInfo heading="Price" type='currency' value={_.get(itemDetails, 'inventoryItem.price', '-')} />
            <RenderInfo heading="Last Sync Needed" type='date' value={_.get(itemDetails, 'lastSyncNeeded', '-')} />
            <RenderInfo heading="Last Sync Performed" type='date' value={_.get(itemDetails, 'lastSyncPerformed', '-')} />
          </div>
          
        </div>
      </div>
    </div>
  );
};

export default InventoryItem;
